import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  BellIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  HomeIcon,
  PencilSquareIcon,
  PhotoIcon,
  UserGroupIcon,
  ArrowLeftOnRectangleIcon,
  MoonIcon,
  SunIcon,
  Bars3Icon,
  XMarkIcon,
  Squares2X2Icon,
  QueueListIcon,
  RectangleStackIcon,
  BookOpenIcon,
  CreditCardIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { NewspaperIcon } from '@heroicons/react/24/outline';

interface LayoutProps {
  isDark: boolean;
  setIsDark: (value: boolean) => void;
}

const adminNavigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Homepage', href: '/homepage', icon: Squares2X2Icon },
  { name: 'Lesson Builder', href: '/lessons', icon: BookOpenIcon },
  { name: 'Compose', href: '/compose', icon: PencilSquareIcon },
  { name: 'Templates', href: '/templates', icon: DocumentDuplicateIcon },
  { name: 'Materials', href: '/materials', icon: DocumentTextIcon },
  { name: 'Media', href: '/media', icon: PhotoIcon },
  { name: 'Subjects', href: '/subjects', icon: DocumentTextIcon },
  { name: 'Subjects HTML', href: '/subjects-html', icon: DocumentTextIcon },
  { name: 'News', href: '/news', icon: NewspaperIcon },
  { name: 'Topics', href: '/topics', icon: QueueListIcon },
  { name: 'Chapters', href: '/chapters', icon: RectangleStackIcon },
  { name: 'MCQuiz', href: '/mcquiz/tests', icon: AcademicCapIcon },
  { name: 'Website', href: '/analytics', icon: ChartBarIcon },
  { name: 'Users', href: '/users', icon: UserGroupIcon },
  { name: 'Billing', href: '/billing', icon: CreditCardIcon },
  { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
];

const staffNavigation = [
  { name: 'Media', href: '/media', icon: PhotoIcon },
  { name: 'News Load', href: '/news', icon: NewspaperIcon },
];

export default function Layout({ isDark, setIsDark }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, role } = useAuth();
  const navigate = useNavigate();

  // Select navigation based on role
  const navigation = role === 'admin' ? adminNavigation : staffNavigation;

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 lg:hidden z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-72 h-screen bg-white dark:bg-gray-800 transform transition-transform lg:translate-x-0 lg:static lg:inset-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center gap-3 px-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
              <BellIcon className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Clarity Admin
            </span>
            <button
              onClick={() => setSidebarOpen(false)}
              className="ml-auto lg:hidden"
            >
              <XMarkIcon className="w-6 h-6 text-gray-500" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 px-4 py-4">
            {role === 'admin' && (
              <a href={import.meta.env.VITE_OWNER_REVIEW_URL || '/owner-review/'}
                 className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                <BookOpenIcon className="w-5 h-5" />
                <span className="font-medium">Article Review</span>
              </a>
            )}
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.name}</span>
              </NavLink>
            ))}
          </nav>

          {/* User section */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {user?.email}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {role === 'admin' ? 'Administrator' : 'Staff Member'}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-500 hover:text-red-600 transition-colors"
                title="Logout"
              >
                <ArrowLeftOnRectangleIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col h-screen overflow-auto">
        {/* Top bar */}
        <header className="flex-shrink-0 flex h-16 items-center gap-4 border-b border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 backdrop-blur px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden"
          >
            <Bars3Icon className="w-6 h-6 text-gray-500" />
          </button>

          <div className="flex-1" />

          {/* Theme toggle */}
          <button
            onClick={() => setIsDark(!isDark)}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            {isDark ? (
              <SunIcon className="w-5 h-5" />
            ) : (
              <MoonIcon className="w-5 h-5" />
            )}
          </button>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <div className="p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
